import { ArgumentException } from '../../../exceptions/argument-exception';
import { Currency } from '../../../models/serializable/currency';
import { EventTicket } from '../../../models/serializable/event-ticket';
import { GenericOrder } from '../../../models/serializable/orders/generic-order';
import { GenericOrderTicketEntry } from '../../../models/serializable/orders/generic-order-entries/generic-order-ticket-entry';
import { GenericOrderTicketSupplementEntry } from '../../../models/serializable/orders/generic-order-entries/generic-order-ticket-supplement-entry';
import { GenericOrderEntry, GenericOrderEntryStatus } from '../../../models/serializable/orders/generic-order-entry';
import { Price } from '../../../models/serializable/price';
import { TicketSupplementRef } from '../../../models/serializable/ticket-supplement-ref';
import { GenericOrderDiscountBreakdownEntry } from '../discount-breakdown';
import { GenericOrderFeeBreakdownEntry } from '../fee-breakdown';
import { IGenericOrderPaymentHelperAdapter } from './generic-order-payment-helper-adapter';

export class TicketSupplementEntryPaymentHelperAdapter extends IGenericOrderPaymentHelperAdapter {
  public readonly orderEntryTypeId: string = GenericOrderTicketSupplementEntry.TYPE_ID;

  static verifyType(dm: IGenericOrderPaymentHelperAdapter): boolean {
    return dm instanceof TicketSupplementEntryPaymentHelperAdapter;
  }

  static assertType(dm: IGenericOrderPaymentHelperAdapter): dm is TicketSupplementEntryPaymentHelperAdapter {
    return dm instanceof TicketSupplementEntryPaymentHelperAdapter;
  }

  getCurrency(order: GenericOrder, orderEntry: GenericOrderEntry): Currency | null {
    if(!GenericOrderTicketSupplementEntry.assertType(orderEntry)) {
      throw new ArgumentException('order', 'Order entry type is not valid');
    }

    const parentEntry = this.findParentOrderEntry(order, orderEntry);
    return parentEntry != null ? this.getCurrencyOfParent(parentEntry) : null;
  }

  calculateDiscountBreakdown(
      order: GenericOrder,
      orderEntry: GenericOrderEntry,
      _orderEntryIndex: number
  ): GenericOrderDiscountBreakdownEntry[] | null {
    if(!GenericOrderTicketSupplementEntry.assertType(orderEntry)) {
      throw new ArgumentException('order', 'Order entry type is not valid');
    }

    return null;
  }

  calculateFeeBreakdown(
      order: GenericOrder,
      orderEntry: GenericOrderEntry,
      _orderEntryIndex: number
  ): GenericOrderFeeBreakdownEntry[] | null {
    if(!GenericOrderTicketSupplementEntry.assertType(orderEntry)) {
      throw new ArgumentException('order', 'Order entry type is not valid');
    }

    return null;
  }

  calculateCheckoutPrice(order: GenericOrder, orderEntry: GenericOrderEntry): Price | null {
    if(!GenericOrderTicketSupplementEntry.assertType(orderEntry)) {
      throw new ArgumentException('order', 'Order entry type is not valid');
    }

    return this.calculatePrice({ order, orderEntry });
  }

  calculateIncome(order: GenericOrder, orderEntry: GenericOrderEntry): Price | null {
    if(!GenericOrderTicketSupplementEntry.assertType(orderEntry)) {
      throw new ArgumentException('order', 'Order entry type is not valid');
    }

    return this.calculatePrice({ order, orderEntry });
  }

  calculateIncomeWithoutDiscount(order: GenericOrder, orderEntry: GenericOrderEntry): Price | null {
    if(!GenericOrderTicketSupplementEntry.assertType(orderEntry)) {
      throw new ArgumentException('order', 'Order entry type is not valid');
    }

    return this.calculatePrice({ order, orderEntry });
  }

  calculateDiscount(orderEntry: GenericOrderEntry): Price | null {
    if(!GenericOrderTicketSupplementEntry.assertType(orderEntry)) {
      throw new ArgumentException('order', 'Order entry type is not valid');
    }

    return null;
  }

  calculateCheckoutPriceForDiscountProvider(orderEntry: GenericOrderEntry): Price | null {
    if(!GenericOrderTicketSupplementEntry.assertType(orderEntry)) {
      throw new ArgumentException('order', 'Order entry type is not valid');
    }

    return null;
  }

  canCreateRefund(order: GenericOrder, orderEntry: GenericOrderEntry): boolean | null {
    if(!GenericOrderTicketSupplementEntry.assertType(orderEntry)) {
      throw new ArgumentException('order', 'Order entry type is not valid');
    }

    if(orderEntry.status !== GenericOrderEntryStatus.Refunded) {
      return false;
    }

    return orderEntry.refunds.length <= 0;
  }

  calculateMaxSimulatedRefund(order: GenericOrder, orderEntry: GenericOrderEntry): Price | null {
    if(!GenericOrderTicketSupplementEntry.assertType(orderEntry)) {
      throw new ArgumentException('order', 'Order entry type is not valid');
    }

    return this.calculateIncome(order, orderEntry);
  }

  private calculatePrice({ order, orderEntry }: {
      order: GenericOrder,
      orderEntry: GenericOrderTicketSupplementEntry,
  }): Price | null {
    const parentEntry = this.findParentOrderEntry(order, orderEntry);
    if(parentEntry == null) {
      return null;
    }

    const currency = this.getCurrencyOfParent(parentEntry);
    if(currency == null) {
      return null;
    }

    let orderEntryPrice = Price.zero(currency);

    for(const supplementRef of orderEntry.supplements) {
      const ticket = this.findTicketOfParentOrderEntry(parentEntry, supplementRef);
      if(ticket == null || ticket.supplement == null) {
        return null;
      }
      if(ticket.supplement.template.price.currency !== currency) {
        return null;
      }

      const supplementPrice = ticket.supplement.template.price;
      orderEntryPrice = orderEntryPrice.add(supplementPrice);
    }

    return orderEntryPrice;
  }

  private getCurrencyOfParent(orderEntry: GenericOrderTicketEntry): Currency | null {
    if(orderEntry.tickets.length <= 0) {
      return null;
    }

    return orderEntry.tickets[0].template.price.currency;
  }

  private findParentOrderEntry(order: GenericOrder, orderEntry: GenericOrderTicketSupplementEntry): GenericOrderTicketEntry | null {
    for(const entry of order.entries) {
      if(GenericOrderTicketEntry.assertType(entry)) {
        if(entry.uniqueId === orderEntry.parentEntryId) {
          return entry;
        }
      }
    }

    return null;
  }

  private findTicketOfParentOrderEntry(orderEntry: GenericOrderTicketEntry, supplementRef: TicketSupplementRef): EventTicket | null {
    if(supplementRef.orderEntryId === orderEntry.uniqueId) {
      for(const ticket of orderEntry.tickets) {
        if(ticket.uniqueId === supplementRef.ticketId) {
          return ticket;
        }
      }
    }

    return null;
  }
}
